import { useId, useState } from 'react';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { sinhMa } from '../../../lib/ma-tu-sinh';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { MA_TOI_DA, TIEN_TO_MA_UNG_DUNG } from '../constants';
import { loiTheoTruong, loiTuApi } from '../nghiep-vu/cau-hinh';
import type { UngDung } from '../types';

/**
 * Hộp tạo hoặc sửa ứng dụng: tên và webhook URL.
 *
 * Mã tự sinh từ tên lúc tạo (ADR-UI-0014) và **không đổi được** sau đó: mã nằm trong chuỗi dẫn xuất bí mật webhook
 * (ADR-0085 mục 2), đổi mã là đổi bí mật của đối tác mà không ai bấm "xoay". Lỗi SSRF của URL (`#/webhookUrl`) và
 * lỗi trùng mã (`#/ma`, đặt dưới ô tên kèm cách sửa) nằm cạnh ô; hộp không đóng khi lỗi.
 */
export function FormUngDung({
  open,
  ungDung,
  dangGui,
  onClose,
  onGui,
}: {
  open: boolean;
  /** `null`: tạo mới. */
  ungDung: UngDung | null;
  dangGui: boolean;
  onClose: () => void;
  onGui: (du: { ma: string; ten: string; webhookUrl: string }) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const [ten, setTen] = useState(ungDung?.ten ?? '');
  const [url, setUrl] = useState(ungDung?.webhookUrl ?? '');
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<ApiError | null>(null);

  const moi = ungDung === null;
  const ma = moi ? sinhMa(ten, { tienTo: TIEN_TO_MA_UNG_DUNG, toiDa: MA_TOI_DA }) : ungDung.ma;
  const loi = loiTheoTruong(loiTuApi(loiServer?.details));
  const loiTen =
    (daGui && !ten.trim() ? t('ekyc.ungDung.canTen') : undefined) ??
    (ten.trim() && !ma ? t('ekyc.quyTrinh.tenKhongSinhMa') : undefined) ??
    (loi['#/ma'] ? t('ekyc.ungDung.trungMa', { ma }) : undefined) ??
    loi['#/ten'];
  const loiChung = loiServer && Object.keys(loi).length === 0 ? dichLoi(loiServer) : null;

  const gui = async () => {
    setDaGui(true);
    if (!ten.trim() || !ma || dangGui) return;
    setLoiServer(null);
    try {
      await onGui({ ma, ten: ten.trim(), webhookUrl: url.trim() });
    } catch (e) {
      setLoiServer(e as ApiError);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={moi ? t('ekyc.ungDung.taoTieuDe') : t('ekyc.ungDung.suaTieuDe', { ten: ungDung.ten })}
      width={560}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>{t('chung.huy')}</Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dangGui}>
              {moi
                ? dangGui ? t('ekyc.ungDung.dangTao') : t('ekyc.ungDung.taoUngDung')
                : dangGui ? t('chung.dangLuu') : t('ekyc.ungDung.luuUngDung')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('ekyc.ungDung.ten')} required error={loiTen}>
            {(p) => (
              <Input
                {...p}
                autoFocus
                value={ten}
                maxLength={200}
                status={p['aria-invalid'] ? 'error' : undefined}
                onChange={(e) => { setTen(e.target.value); setLoiServer(null); }}
              />
            )}
          </Field>
          <Field label={t('ekyc.ungDung.ma')} hint={moi ? t('ekyc.quyTrinh.maTuSinh') : t('ekyc.ungDung.maKhongDoi')}>
            {(p) => <Input {...p} readOnly value={ma} className="font-mono" placeholder={t('ekyc.quyTrinh.maChoTen')} />}
          </Field>
        </div>
        <Field label={t('ekyc.ungDung.webhookUrl')} hint={t('ekyc.ungDung.webhookUrlGoiY')} error={loi['#/webhookUrl']}>
          {(p) => (
            <Input
              {...p}
              type="url"
              inputMode="url"
              value={url}
              maxLength={2000}
              placeholder="https://"
              status={p['aria-invalid'] ? 'error' : undefined}
              onChange={(e) => { setUrl(e.target.value); setLoiServer(null); }}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}
